/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import upperFirst from 'lodash/upperFirst';

import { token } from '@atlaskit/tokens';

import { getTextColorForBackground } from '../get-text-color-for-background';
import type { PaletteGroup } from '../grouped-tokens';
import { hexToRGBA } from '../hex-to-rgba';
import { listItemStyleReset, listStyleReset } from '../index';

import CopyButton from './copy-button';

const subgroupsListStyles = css({
	display: 'flex',
	flexWrap: 'wrap',
	marginInlineEnd: `${token('space.negative.050')}`,
	marginInlineStart: `${token('space.negative.050')}`,
});

const listItemStyles = css({
	display: 'flex',
	padding: `${token('space.100')} ${token('space.100')} ${token('space.100')} ${token('space.200')}`,
	justifyContent: 'space-between',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.token-alt-values': {
		transition: 'opacity 150ms',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:hover .token-alt-values, &:focus-within .token-alt-values': {
		opacity: 1,
	},
});

const copyButtonContainerStyles = css({
	display: 'inline-grid',
	gap: token('space.050'),
	gridTemplateColumns: '1fr 1fr',
	opacity: 0,
});

const subgroupStyles = (isNeutralColorGroup: boolean) =>
	css({
		minWidth: '50%',
		flex: '1 1 auto',
		marginBlockEnd: token('space.300'),
		paddingInlineEnd: token('space.050'),
		paddingInlineStart: token('space.050'),
		'@media (min-width: 1241px)': {
			minWidth: '33%',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			maxWidth: isNeutralColorGroup ? 'none' : '33%',
		},
	});

const subgroupTitleStyles = css({
	// Override c11n specificity issue
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'p&': {
		fontWeight: token('font.weight.bold'),
		marginBlockEnd: token('space.150'),
		marginBlockStart: token('space.250'),
	},
});

const subGroupStyles = css({
	//eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('radius.small', '3px'),
	paddingInlineEnd: token('space.300'),
	paddingInlineStart: token('space.300'),
});

const SubGroup = (group: PaletteGroup): React.JSX.Element => (
	<div
		css={[
			group.name.includes('neutral') && subGroupStyles,
			// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
			group.name === 'light mode neutrals' && {
				// Must not use Design Tokens as the alpha tokens need to cast onto this
				backgroundColor: '#FAFBFC',
			},
			// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
			group.name === 'dark mode neutrals' && {
				// Must not use Design Tokens as the alpha tokens need to cast onto this
				backgroundColor: '#161A1D',
			},
		]}
	>
		{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */}
		<ul css={[listStyleReset, subgroupsListStyles]}>
			{group.subgroups.map((subgroup) => (
				<li
					key={subgroup.name}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
					css={[listItemStyleReset, subgroupStyles(group.name.includes('neutral'))]}
				>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p
						css={[
							subgroupTitleStyles,
							group.name === 'light mode neutrals' &&
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
								css({
									// Must not use Design Tokens as it casts onto a hardcoded surface
									color: '#172B4D',
								}),
							group.name === 'dark mode neutrals' &&
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
								css({
									// Must not use Design Tokens as it casts onto a hardcoded surface
									color: '#C7D1DB',
								}),
						]}
					>
						{upperFirst(subgroup.name)}
					</p>
					{subgroup.tokens.length > 0 ? (
						<ul
							css={[
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
								listStyleReset,
								// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
								{
									//eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
									borderRadius: token('radius.small', '3px'),
									overflow: 'hidden',
								},
							]}
						>
							{subgroup.tokens.map((t) => {
								const tokenName = t.path[t.path.length - 1];

								const color = getTextColorForBackground(t.value, {
									hardcodedSurface: group.name.includes('neutral')
										? (group.name.replace(' mode neutrals', '') as 'light' | 'dark')
										: undefined,
								});

								return (
									<li
										key={t.value}
										css={[
											// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
											listItemStyleReset,
											listItemStyles,
											// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
											css({
												// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
												backgroundColor: t.value,
												// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
												color: color,
											}),
										]}
									>
										<CopyButton value={tokenName}>{tokenName}</CopyButton>
										<div
											// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
											className="token-alt-values"
											css={copyButtonContainerStyles}
										>
											<CopyButton value={t.value}>Hex</CopyButton>
											<CopyButton value={hexToRGBA(t.value)}>RGB</CopyButton>
										</div>
									</li>
								);
							})}
						</ul>
					) : null}
				</li>
			))}
		</ul>
	</div>
);

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export default SubGroup;
